import { useDocumentTitle } from '@/app/document-title';
import { Button } from '@mgrvtechgroup/admin-ds';
import { KeyRoundIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { safeReturnTo } from './config';
import { browserNavigation } from './property-access-guard';
import { operatorSession, useOperatorSession } from './runtime';
import type { OperatorSession } from './session';

export function OperatorLoginPage({ session = operatorSession }: { session?: OperatorSession }) {
  useDocumentTitle();
  const sessionState = useOperatorSession(session);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState(false);
  const [loginPending, setLoginPending] = useState(false);
  const pendingRef = useRef(false);
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        session.cancelLoginPreparation();
      }
    };
  }, [session]);
  const returnTo = useMemo(() => {
    try {
      const values = searchParams.getAll('returnTo');
      const candidate = values.length === 1 ? values[0] : undefined;
      return safeReturnTo(candidate ?? '/properties');
    } catch {
      return '/properties';
    }
  }, [searchParams]);
  const callbackError = searchParams.get('error') === 'callback';
  const mustLogin =
    callbackError ||
    sessionState.status === 'authenticating' ||
    (sessionState.status === 'unauthenticated' && sessionState.generation > 0);
  const startLogin = async () => {
    if (loginPending || sessionState.status === 'authenticating') return;
    setLoginError(false);
    const generation = session.getSnapshot().generation + 1;
    pendingRef.current = true;
    setLoginPending(true);
    try {
      const url = await session.beginLogin(returnTo, window.location.origin);
      const current = session.getSnapshot();
      pendingRef.current = false;
      if (mountedRef.current) setLoginPending(false);
      if (
        mountedRef.current &&
        current.generation === generation &&
        current.status === 'authenticating'
      )
        browserNavigation.assign(url);
    } catch {
      pendingRef.current = false;
      if (mountedRef.current) {
        setLoginPending(false);
        setLoginError(true);
      }
    }
  };
  const cancel = () => {
    void navigate(returnTo, { replace: true });
  };
  const status =
    sessionState.status === 'not-configured'
      ? '로그인을 사용할 수 없습니다. 관리자에게 문의해 주세요.'
      : callbackError || sessionState.reason === 'invalid-callback'
        ? '로그인 정보를 확인하지 못했습니다. 다시 로그인해 주세요.'
        : sessionState.reason === 'expired' || sessionState.reason === 'failed-refresh'
          ? '세션이 만료되었습니다. 다시 로그인해 주세요.'
          : '인증 확인 중입니다.';

  return (
    <main className="bg-muted/30 box-border flex min-h-screen w-full min-w-0 items-center justify-center p-4">
      <section
        aria-labelledby="operator-login-title"
        className="bg-background box-border w-full max-w-[calc(100vw-2rem)] min-w-0 rounded-xl border p-6 shadow-sm sm:max-w-md sm:p-8"
      >
        <div className="mb-6 flex items-center gap-3">
          <span className="bg-foreground text-background flex size-10 items-center justify-center rounded-lg">
            <KeyRoundIcon aria-hidden="true" className="size-5" />
          </span>
          <div>
            <p className="caption text-muted-foreground">MGRV</p>
            <p className="label">Core Admin</p>
          </div>
        </div>
        <h1 id="operator-login-title" className="heading-lg">
          운영자 로그인
        </h1>
        <p role="status" aria-live="polite" className="body-sm text-muted-foreground mt-2">
          {status}
        </p>
        {loginError && (
          <p role="alert" className="body-sm text-destructive mt-4">
            로그인을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        )}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Button
            className="min-h-11 w-full flex-1 sm:w-auto"
            disabled={loginPending || sessionState.status === 'authenticating'}
            onClick={() => void startLogin()}
          >
            {loginPending || sessionState.status === 'authenticating' ? '인증 확인 중' : '로그인'}
          </Button>
          {!mustLogin && (
            <Button
              className="min-h-11 w-full sm:w-auto"
              disabled={loginPending}
              variant="outline"
              onClick={cancel}
            >
              업무 화면으로 돌아가기
            </Button>
          )}
        </div>
      </section>
    </main>
  );
}
